/**
 * BatchPicker Component
 *
 * FIFO batch selector for inventory materials.
 * Shows available batches ordered by date (oldest first).
 *
 * @module pages/technician/materials/BatchPicker
 */

import React, { useState, useEffect } from 'react';
import { Spinner } from 'react-bootstrap';
import { useAuth } from '../../../contexts/AuthContext';
import { getAvailableBatches } from '../../../services/materialRequestService';
import { formatDate } from '../../../utils/formatters';
import PropTypes from 'prop-types';

const BatchPicker = ({
  inventoryId,
  requestedQuantity = 0,
  onBatchInfo,
  unit = 'unidad',
}) => {
  // Internal purchase-cost gate.
  //
  // `inventory_batches.unit_cost` is what ProMedic PAID for the material. The
  // component already fetches it (the FIFO preview needs the batch rows) and
  // the file already carried the comment "admin-only: unit_cost hidden from
  // technicians" at the quantity label below — but nothing enforced it: the
  // derived `estimatedCost` was handed to the parent unconditionally and
  // printed under the quantity picker for every technician in the field.
  //
  // Jorge's standing decision: the technician does NOT see internal cost; the
  // supervisor DOES see budget and spend. Same allowlist and same reasoning as
  // `canViewInternalCosts` in src/pages/projects/ProjectDetail.js:99 (#500 /
  // #518) — an allowlist rather than `!isTechnician`, so an unloaded profile or
  // a future role (Operario, Mensajero — #463) fails CLOSED.
  const { isAdmin, isSupervisor } = useAuth();
  const canViewUnitCost = isAdmin || isSupervisor;
  const [batches, setBatches] = useState([]);
  const [totalAvailable, setTotalAvailable] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch available batches
  useEffect(() => {
    const fetchBatches = async () => {
      if (!inventoryId) return;

      setIsLoading(true);
      setError(null);

      try {
        const { data, totalAvailable: total, error: fetchError } = await getAvailableBatches(inventoryId);

        if (fetchError) {
          setError('Error al cargar lotes');
          setBatches([]);
          setTotalAvailable(0);
        } else {
          setBatches(data || []);
          setTotalAvailable(total || 0);
        }
      } catch (err) {
        console.error('Fetch batches error:', err);
        setError('Error al cargar lotes');
        setBatches([]);
        setTotalAvailable(0);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBatches();
  }, [inventoryId]);

  // Calculate FIFO consumption preview
  useEffect(() => {
    if (batches.length === 0 || requestedQuantity <= 0) {
      onBatchInfo?.({
        sufficient: false,
        consumptionPlan: [],
        estimatedCost: canViewUnitCost ? 0 : null,
        canViewUnitCost,
        totalAvailable,
      });
      return;
    }

    let remaining = requestedQuantity;
    const consumptionPlan = [];
    let estimatedCost = 0;

    for (const batch of batches) {
      if (remaining <= 0) break;

      const consumeFromBatch = Math.min(remaining, batch.quantity_available);
      const batchCost = consumeFromBatch * batch.unit_cost;

      // The cost fields travel only when the viewer may see them. Emitting them
      // and hiding them at render time would leave the number one React DevTools
      // click (or one future widget) away from the technician.
      consumptionPlan.push({
        batchId: batch.id,
        // #522 — los tres campos de abajo apuntaban a columnas que NO EXISTEN
        // (`batch_number`, `received_at`). Como la consulta que los pedía
        // devolvía 400 y el servicio lo tragaba como lista vacía, nunca llegó
        // un lote hasta acá y el error jamás se hizo visible. Los reales son
        // `lot_number` y `created_at`.
        lotNumber: batch.lot_number,
        quantity: consumeFromBatch,
        receivedAt: batch.created_at,
        ...(canViewUnitCost
          ? { unitCost: batch.unit_cost, totalCost: batchCost }
          : {}),
      });

      estimatedCost += batchCost;
      remaining -= consumeFromBatch;
    }

    const sufficient = remaining <= 0;

    onBatchInfo?.({
      sufficient,
      consumptionPlan,
      estimatedCost: canViewUnitCost ? Math.round(estimatedCost * 100) / 100 : null,
      canViewUnitCost,
      totalAvailable,
    });
  }, [batches, requestedQuantity, totalAvailable, onBatchInfo, canViewUnitCost]);

  // Calculate which batch will be consumed for FIFO indicator
  const getBatchConsumptionStatus = (batch, index) => {
    if (requestedQuantity <= 0) return { willConsume: false, quantity: 0 };

    let remaining = requestedQuantity;
    for (let i = 0; i < index; i++) {
      remaining -= batches[i].quantity_available;
    }

    if (remaining <= 0) return { willConsume: false, quantity: 0 };

    const consumeFromBatch = Math.min(remaining, batch.quantity_available);
    return {
      willConsume: true,
      quantity: consumeFromBatch,
      isPartial: consumeFromBatch < batch.quantity_available,
    };
  };

  if (isLoading) {
    return (
      <div className="batch-picker">
        <div className="loading-container" style={{ padding: '24px' }}>
          <Spinner animation="border" size="sm" />
          <span className="loading-text">Cargando lotes...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="batch-picker">
        <div className="batch-insufficient-message">
          <i className="bi bi-exclamation-circle"></i>
          <span>{error}</span>
        </div>
      </div>
    );
  }

  if (batches.length === 0) {
    return (
      <div className="batch-picker">
        <div className="batch-insufficient-message">
          <i className="bi bi-inbox"></i>
          <span>No hay lotes disponibles para este material</span>
        </div>
      </div>
    );
  }

  const isSufficient = totalAvailable >= requestedQuantity;

  return (
    <div className="batch-picker">
      <div className="batch-picker-title">
        <i className="bi bi-layers"></i>
        <span>Lotes Disponibles (FIFO)</span>
      </div>

      <div className="batch-list">
        {batches.map((batch, index) => {
          const consumption = getBatchConsumptionStatus(batch, index);

          return (
            <div
              key={batch.id}
              className={`batch-item ${consumption.willConsume ? 'selected' : ''}`}
            >
              <div className={`batch-order ${index > 0 ? 'secondary' : ''}`}>
                {index + 1}
              </div>
              <div className="batch-info">
                <div className="batch-number">
                  {batch.lot_number || `Lote #${index + 1}`}
                </div>
                <div className="batch-details">
                  {/*
                    #522 — acá se leían `batch_number`, `received_at`,
                    `expiry_date` y `purchase.supplier.name`. Ninguno existe:
                    las columnas reales son `lot_number`, `created_at`,
                    `expiration_date` y `location`, y el proveedor cuelga de
                    `purchase_id`, no de un `supplier_id` embebible. Se
                    reemplazan por los reales y el proveedor se deja afuera
                    hasta que se haga el join por compra — mejor no mostrarlo
                    que mostrar un hueco permanente.
                  */}
                  <span>Ingreso: {formatDate(batch.created_at)}</span>
                  {batch.location && <span> | {batch.location}</span>}
                  {batch.expiration_date && (
                    <span> | Vence: {formatDate(batch.expiration_date)}</span>
                  )}
                </div>
              </div>
              <div className="batch-quantity">
                <div className="batch-quantity-value">
                  {consumption.willConsume ? (
                    <span>
                      <strong>{consumption.quantity}</strong>
                      <span style={{ color: '#6c757d', fontWeight: 'normal' }}>
                        {' '}/ {batch.quantity_available}
                      </span>
                    </span>
                  ) : (
                    batch.quantity_available
                  )}
                </div>
                <div className="batch-quantity-label">
                  {unit}
                  {/*
                    This row deliberately shows quantity only — never
                    `batch.unit_cost`. The comment that used to sit here said
                    "admin-only: unit_cost hidden from technicians" while the
                    derived total was being printed to everyone by the parent
                    form; the gate now lives in `canViewUnitCost` above and is
                    enforced, not just described.
                  */}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {requestedQuantity > 0 && isSufficient && (
        <div className="batch-fifo-note">
          <i className="bi bi-info-circle"></i>
          <span>
            Se consumiran {requestedQuantity} {unit} de los lotes mas antiguos (FIFO)
          </span>
        </div>
      )}

      {requestedQuantity > 0 && !isSufficient && (
        <div className="batch-insufficient-message">
          <i className="bi bi-exclamation-triangle"></i>
          <span>
            Stock insuficiente. Disponible: {totalAvailable} {unit}, Solicitado: {requestedQuantity} {unit}
          </span>
        </div>
      )}
    </div>
  );
};

BatchPicker.propTypes = {
  inventoryId: PropTypes.string.isRequired,
  requestedQuantity: PropTypes.number,
  onBatchInfo: PropTypes.func,
  unit: PropTypes.string,
};

export default BatchPicker;
